import { get, last } from 'lodash';
import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api } from 'services/api';

type Order = 'DESC' | 'ASC';

type Filters = Record<string, unknown>;
type Config = {
  limit?: number | null;
};

type FetchFormsParams = {
  filters: Filters;
  order?: Order;
  config: Config;
  pageParam?: number;
};

type FetchFormsResponse = {
  data: unknown[];
  nextOffset?: number | null;
  count: number;
};
/**
 *
 * Api requests for React-Query
 *
 */

const fetchForms = async ({
  filters,
  order,
  config,
  pageParam,
}: FetchFormsParams) => {
  const { borrowerId } = filters;
  let formType = get(filters, 'formType');
  const isIdrConsolidation = formType === 'idrConsolidation';
  formType = isIdrConsolidation ? 'idr' : formType;
  const result = await api.get<FetchFormsResponse>('/forms', {
    params: {
      borrower_id: borrowerId === '' ? null : borrowerId,
      status: get(filters, 'status'),
      owner_uuid: get(filters, 'ownerUuid'),
      partner_id: get(filters, 'partner'),
      form_type: formType,
      fax_submission_status: get(filters, 'faxSubmissionStatus'),
      limit: get(config, 'limit', 20),
      offset: pageParam ?? 0,
      order,
      is_idr_consolidation: isIdrConsolidation,
    },
  });

  return {
    forms: result.data.data,
    nextOffset: result.data.nextOffset,
    count: result.data.count,
  };
};

const fetchForm = async ({ formId }: { formId: string }) => {
  const formRes = await api.get(`/forms/${formId}`);

  return formRes?.data.data;
};

const putFormMetadata = async ({
  formId,
  payload,
}: {
  // Callers (e.g. FormMetadata.tsx) pass the numeric `form.id` from the API,
  // so we accept either — useForm keys on `formId.toString()` and the URL
  // template stringifies on its own.
  formId: string | number;
  payload: unknown;
}) => {
  const formRes = await api.put(`/forms/${formId}/metadata`, payload);

  return formRes?.data.data;
};

const putFormStatusAsSigned = async ({
  formId,
}: {
  formId: string | number;
}) => {
  const formRes = await api.put(`/forms/${formId}/status`);

  return formRes?.data.data;
};

const postFormDownloadURL = async ({
  formId,
  documentId,
}: {
  formId: string;
  documentId: string;
}) => {
  const fileUrlRes = await api.post(`/forms/${formId}/${documentId}`);

  return fileUrlRes?.data.data;
};

/**
 *
 * Custom Hooks with React-Query
 *
 */

type UseFormsParams = {
  filters?: Filters;
  order?: Order;
  config?: Config;
};

export const useForms = (
  { filters = {}, order, config = { limit: 20 } }: UseFormsParams = {
    filters: {},
    config: { limit: 20 },
  },
) => {
  const {
    data,
    isPending: isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['forms', filters, order, config],
    queryFn: ({ pageParam }) =>
      fetchForms({ filters, order, config, pageParam }),
    initialPageParam: 0,
    getNextPageParam: lastPage => {
      return lastPage.nextOffset ?? undefined;
    },
    refetchOnWindowFocus: false,
  });

  const resultArray = data?.pages;

  const forms = useMemo(
    () => resultArray?.flatMap(({ forms }) => forms),
    [resultArray],
  );

  const formsTotalCount = last(resultArray)?.count ?? 0;

  return {
    forms,
    formsTotalCount,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useForm = ({ formId }: { formId: string | number }) =>
  useQuery({
    queryKey: ['form', formId.toString()],
    queryFn: () => fetchForm({ formId: formId.toString() }),
  });

export const useMutateForm = ({ fromOverlay = false } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['putFormMetadata'],
    mutationFn: putFormMetadata,
    onSuccess: async (_, { formId }) => {
      // formId must be coerced to string: useForm keys its query as
      // ['form', formId.toString()], and react-query treats numbers and
      // strings as distinct keys, so without coercion the invalidation
      // silently misses and the UI stays stale (sc-49526).
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: fromOverlay ? ['form'] : ['form', formId.toString()],
        }),
        queryClient.invalidateQueries({ queryKey: ['forms'] }),
      ]);
    },
  });
};

export const useMutateFormSigned = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['putFormStatusAsSigned'],
    mutationFn: putFormStatusAsSigned,
    onSuccess: async (_, { formId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['form', formId.toString()],
        }),
        queryClient.invalidateQueries({ queryKey: ['forms'] }),
      ]);
    },
  });
};

export const useFormDownloadURL = () =>
  useMutation({
    mutationFn: postFormDownloadURL,
  });
